import { useCallback, useEffect, useState } from "react";
import { container } from "tsyringe";
import Button, { EButtonVariant, EButtonColor, EButtonSize } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import PostCard from "./PostCard";
import { EPostCardModal, usePostCardModal } from "./PostCardModal";
import CreatePostModal from "./PostCardModal/CreatePostModal";
import EditPostModal from "./PostCardModal/EditPostModal";
import DeletePostModal from "./PostCardModal/DeletePostModal";
import { EForumPostCategory } from "common/enums/Project/Config/Forum/EForumPostCategory";
import ForumPostResponseResource from "common/resources/Project/Config/Forum/ForumPostResponseResource";
import { upsert } from "common/utils/array";
import { useProjectId } from "hooks/useProjectId";
import ForumPostService from "services/projects/ForumPostService";
import classes from "./classes.module.scss";

const forumPostService = container.resolve(ForumPostService);

export default function Forum() {
	const { posts, removePost, upsertPost } = useGeneralPosts();
	const { selectedPost, isModalOpen, onCloseModal, onOpenCreateModal, onOpenEditModal, onOpenDeleteModal } = usePostCardModal();

	const onCreated = useCallback(
		(post: ForumPostResponseResource) => {
			upsertPost(post);
			onCloseModal();
		},
		[onCloseModal, upsertPost],
	);

	const onEdited = useCallback(
		(post: ForumPostResponseResource) => {
			upsertPost(post);
			onCloseModal();
		},
		[onCloseModal, upsertPost],
	);

	const onDeleted = useCallback(
		(deletedPost: Pick<ForumPostResponseResource, "id">) => {
			removePost(deletedPost);
			onCloseModal();
		},
		[onCloseModal, removePost],
	);

	return (
		<div className={classes["root"]}>
			<div className={classes["title-container"]}>
				<Typography type="h3" weight="bold">
					{I18n.asset.pages.professional_space.tabs.forum.title}
				</Typography>
				<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} size={EButtonSize.SMALL} onClick={onOpenCreateModal}>
					{I18n.asset.pages.professional_space.tabs.forum.create_post}
				</Button>
			</div>

			<div className={classes["posts-container"]}>
				{posts.map((post) => (
					<PostCard key={post.id} post={post} onEditClick={() => onOpenEditModal(post)} onDeleteClick={() => onOpenDeleteModal(post)} />
				))}
			</div>

			{isModalOpen(EPostCardModal.CREATE_POST) && <CreatePostModal isOpen category={EForumPostCategory.GENERAL} onClose={onCloseModal} onCreated={onCreated} />}
			{isModalOpen(EPostCardModal.EDIT_POST) && selectedPost && <EditPostModal isOpen post={selectedPost} onClose={onCloseModal} onEdited={onEdited} />}
			{isModalOpen(EPostCardModal.DELETE_POST) && selectedPost && <DeletePostModal isOpen post={selectedPost} onClose={onCloseModal} onDeleted={onDeleted} />}
		</div>
	);
}

const useGeneralPosts = () => {
	const { projectId } = useProjectId();
	const [posts, setPosts] = useState<ForumPostResponseResource[]>([]);

	const fetchPosts = useCallback(() => {
		return forumPostService
			.getGeneralPosts(projectId)
			.then(setPosts)
			.catch((error) => {
				console.error(error);
				setPosts([]);
			});
	}, [projectId]);

	const upsertPost = useCallback((post: ForumPostResponseResource) => {
		setPosts((posts) => upsert(posts, post, (p) => p.id === post.id));
	}, []);

	const removePost = useCallback((deletedPost: Pick<ForumPostResponseResource, "id">) => {
		setPosts((posts) => posts.filter((p) => p.id !== deletedPost.id));
	}, []);

	useEffect(() => {
		fetchPosts();
	}, [fetchPosts]);

	return { posts, removePost, upsertPost };
};
